import { isShallowEqual } from "@client/lib/object";
import * as React from "react";
import { initGoogleMaps, loadGoogleScripts } from "./lib";

export interface GoogleMapsOptions {
  address: string;
  address2?: string;
  address3?: string;
  type: string;
  zoom: number;
}

export interface GoogleMapsProps {
  googleKey: string;
  options: GoogleMapsOptions;
}

interface State {
  error: string | null;
  isLoading: boolean;
}

class GoogleMaps extends React.Component<GoogleMapsProps, State> {
  public state: State = {
    error: null,
    isLoading: false
  };
  private _container = React.createRef<HTMLDivElement>();

  public componentDidMount() {
    this._init(this.props);
  }

  public async componentDidUpdate(prevProps: Readonly<GoogleMapsProps>, prevState: Readonly<State>, snapshot?: any) {
    if (this.props.googleKey !== prevProps.googleKey) {
      await this._init(this.props);
    }

    if (!isShallowEqual(this.props.options, prevProps.options)) {
      const container = this._container.current!;
      initGoogleMaps(container, this.props.options);
    }
  }

  public shouldComponentUpdate(nextProps: GoogleMapsProps) {
    return this.props.googleKey !== nextProps.googleKey || !isShallowEqual(this.props.options, nextProps.options);
  }

  public render() {
    return (
      <>
        {!this.props.googleKey ? (
          <p>
            Insert your google key to make map work. Check how to generate your key{" "}
            <a target="_blank" href="https://developers.google.com/maps/documentation/javascript/get-api-key">
              here
            </a>
            . Also please make sure that "Geocoding Api" is enabled.
          </p>
        ) : this.state.error ? (
          <p className="error">{this.state.error}</p>
        ) : (
          <div ref={this._container} style={{ height: "100%", width: "100%" }} />
        )}
      </>
    );
  }

  private _init = async (props: GoogleMapsProps) => {
    if (!props.googleKey || this.state.isLoading) return;

    try {
      await loadGoogleScripts(props.googleKey);
      this.setState({ error: null }, () => {
        const container = this._container.current as HTMLElement;

        initGoogleMaps(container, props.options);

        this.setState({ isLoading: false });
      });
    } catch (e) {
      this.setState({ error: e.message, isLoading: false });
    }
  };
}

export default GoogleMaps;
